import { Container } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useAuthentication } from 'src/features/authentication/context';
import { SITES_MANAGEMENT_PATH } from 'src/routes/config';
import { AccountBillingService } from 'src/services/account-billing-information';
import {
  useGetAccountBillingInformationQuery,
  useGetBillingStatusQuery,
  ACCOUNT_BILLING_INFORMATION_QUERY_KEY,
  BILLING_INFORMATION_STATUS_QUERY_KEY,
} from 'src/hooks/reactQueryApiHooks';
import { useQueryClient } from '@tanstack/react-query';
import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import { ContentLoadingError } from 'src/components/streametric/content-loading-error';
import {
  AccountBillingInformationForm,
  AccountBillingInformationFormValues,
} from '../components/AccountBillingInformationForm';

const AccountBillingInformationPage = () => {
  useDocumentTitle('Streametric - Billing Information');
  const { customerId, isBillingAdminForCustomer } = useAuthentication();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { billingInformationStatus } = useGetBillingStatusQuery({
    customerId: customerId.value ?? '',
  });

  const { accountBillingInformation, isLoading, isError } = useGetAccountBillingInformationQuery({
    customerId: customerId.value ?? '',
  });

  const handleSave = async (values: AccountBillingInformationFormValues) => {
    if (!customerId.value) return;

    try {
      await AccountBillingService.saveAccountBillingInformation(customerId.value, {
        companyInformation: {
          companyName: values.companyName,
          website: values.website || undefined,
          streetAddress1: values.companyStreetAddress1,
          streetAddress2: values.companyStreetAddress2 || undefined,
          city: values.companyCity || undefined,
          state: values.companyState || undefined,
          zipCode: values.companyZipCode || undefined,
          country: values.companyCountry || undefined,
        },
        billingContact: {
          email: values.contactEmail,
          title: values.contactTitle || undefined,
          firstName: values.contactFirstName || undefined,
          middleName: values.contactMiddleName || undefined,
          lastName: values.contactLastName || undefined,
          phoneNumber: values.contactPhoneNumber || undefined,
          mobileNumber: values.contactMobileNumber || undefined,
        },
        billingAddress: values.useDifferentBillingAddress
          ? {
              streetAddress1: values.billingStreetAddress1,
              streetAddress2: values.billingStreetAddress2 || undefined,
              city: values.billingCity,
              state: values.billingState,
              zipCode: values.billingZipCode,
              country: values.billingCountry,
            }
          : undefined,
        autoRenewal: values.autoRenewal,
        authorizeBilling: values.authorizeBilling,
      });
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: [ACCOUNT_BILLING_INFORMATION_QUERY_KEY, customerId.value],
        }),
        queryClient.refetchQueries({
          queryKey: [BILLING_INFORMATION_STATUS_QUERY_KEY, customerId.value],
        }),
      ]);
      enqueueSnackbar('Billing information saved.', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to save billing information.', { variant: 'error' });
    }
  };

  if (isError) {
    return (
      <Container maxWidth="md" disableGutters sx={{ pt: 4 }}>
        <ContentLoadingError contentFailedToLoad="Billing information" />
      </Container>
    );
  }

  const readOnly = !isBillingAdminForCustomer(customerId.value ?? '');

  const showSkipForLater =
    !readOnly &&
    !!billingInformationStatus?.billingConfigurationRequired &&
    billingInformationStatus.billingConfigured === false;

  const handleSkipForLater = () => {
    navigate(`${SITES_MANAGEMENT_PATH}?customerId=${customerId.value}`);
  };

  return (
    <Container maxWidth="md" disableGutters>
      <AccountBillingInformationForm
        defaultValues={accountBillingInformation}
        isLoading={isLoading}
        readOnly={readOnly}
        onSubmit={handleSave}
        onSkipForLater={showSkipForLater ? handleSkipForLater : undefined}
      />
    </Container>
  );
};

export default AccountBillingInformationPage;
